import { Button } from "$components/button/button";
import { useAnalytics } from "$global-providers/analytics-provider/provider";
import {
  type Territory,
  useConfig,
} from "$global-providers/config-provider/provider";
import { COUNTRY_CODES, TERRITORY } from "$routes/_lib/constants";
import useFacility from "$routes/_lib/use-facility";
import { NewFooter } from "$routes/new/_lib/NewFooter";
import NewTitle from "$routes/new/_lib/NewTitle";
import { linkToNewPatientStep2V2 } from "$routes/new/links";
import { yupResolver } from "@hookform/resolvers/yup";
import { type Address, Gender } from "elephant-web/gql";
import { useEffect, useState } from "react";
import { type DefaultValues, FormProvider, useForm } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import { v4 } from "uuid";
import * as Yup from "yup";
import { useRegistrationFormState } from "../_lib/patient-registration-context";
import {
  addressLine,
  dateOfBirthValidation,
} from "../_lib/sharedFormValidation";
import { TermsOfRegistrationDialog } from "../_lib/terms-of-registration-dialog";
import { AddressSection } from "./_lib/AddressSection";
import { GeneralSection } from "./_lib/GeneralSection";

export enum RegistrationSteps {
  TermsOfRegistration = "TermsOfRegistration",
  Step1 = "Step1",
  Step2 = "Step2",
  Step3 = "Step3",
  LinkGuardian = "LinkGuardian",
  Consents = "Consents",
}

export const getNewPatientStep1ValidationSchema = (territory: Territory) =>
  Yup.object({
    newToFacility: Yup.bool().required(),
    firstName: Yup.string().required(),
    lastName: Yup.string().required(),
    middleName:
      territory === TERRITORY.KENYA ? Yup.string().required() : Yup.string(),
    dob: dateOfBirthValidation().required(),
    gender: Yup.mixed<Gender>()
      .oneOf(Object.values(Gender), "Please select a sex")
      .required(),
    address: Yup.object({
      country: addressLine().required(),
      line1: addressLine().required(),
      line2: addressLine().required(),
      line3:
        territory === TERRITORY.NIGERIA
          ? addressLine().required()
          : addressLine(),
      line4: addressLine().required(),
    }).required(),
  });

const getDefaultValues = ({
  facilityAddress,
  territory,
  step1FormValues,
}: {
  facilityAddress: Address;
  territory: Territory;
  step1FormValues?: NewPatientStep1FormValues;
}): DefaultValues<NewPatientStep1FormValues> => {
  let country = COUNTRY_CODES[territory.toUpperCase()];
  // ENG-3226: Must fix bad facilities so can remove this.
  if (facilityAddress?.country && facilityAddress?.country !== "") {
    country = facilityAddress?.country;
  }

  return {
    firstName: "",
    lastName: "",
    middleName: "",
    dob: null,
    gender: null,
    newToFacility: null,
    address: {
      country,
      line1: facilityAddress?.state ?? "",
      line2: facilityAddress?.subCounty ?? "",
      line3: facilityAddress?.ward ?? "",
      line4: "",
    },
    ...step1FormValues,
  };
};

export type NewPatientStep1FormValues = Yup.InferType<
  ReturnType<typeof getNewPatientStep1ValidationSchema>
>;

export const Page: React.FC = () => {
  const { step1FormValues, setStep1FormValues, trackingId, setTrackingId } =
    useRegistrationFormState();
  const { trackEvent } = useAnalytics();
  trackEvent("Patient RegistrationStepStarted", {
    step: RegistrationSteps.TermsOfRegistration,
    trackingId,
  });
  const [patientConsentOpen, setPatientConsentOpen] = useState(
    !step1FormValues,
  );

  const { territory } = useConfig();
  const { data: facilityData } = useFacility();
  const navigate = useNavigate();

  useEffect(() => {
    if (trackingId) {
      return;
    }
    setTrackingId(v4());
  }, [setTrackingId, trackingId]);

  const formMethods = useForm<NewPatientStep1FormValues>({
    defaultValues: getDefaultValues({
      facilityAddress: facilityData?.address,
      territory,
      step1FormValues,
    }),
    resolver: yupResolver(getNewPatientStep1ValidationSchema(territory)),
    mode: "onTouched",
  });

  return (
    <>
      <NewTitle pageNumber={1} />
      <TermsOfRegistrationDialog
        open={patientConsentOpen}
        onClose={() => navigate(-1)}
        onSubmit={() => {
          setPatientConsentOpen(false);
          trackEvent("Patient RegistrationStepStarted", {
            step: RegistrationSteps.Step1,
            trackingId,
          });
        }}
      />
      <FormProvider {...formMethods}>
        <form
          style={{ width: "100%" }}
          name="registerPatient.step1"
          onSubmit={formMethods.handleSubmit((data) => {
            setStep1FormValues(data);
            navigate(linkToNewPatientStep2V2(), {
              state: { trackingId },
            });
          })}
        >
          <GeneralSection
            territory={territory}
            fieldNames={{
              firstName: "firstName",
              middleName: "middleName",
              lastName: "lastName",
              dob: "dob",
              gender: "gender",
              newToFacility: "newToFacility",
            }}
          />
          <AddressSection
            territory={territory}
            fieldNames={{
              country: "address.country",
              addressLine1: "address.line1",
              addressLine2: "address.line2",
              addressLine3: "address.line3",
              addressLine4: "address.line4",
            }}
          />
          <NewFooter>
            <Button type="submit" variant="contained" color="secondary">
              <FormattedMessage
                id="form.page.continue"
                defaultMessage="Continue"
              />
            </Button>
          </NewFooter>
        </form>
      </FormProvider>
    </>
  );
};
